import React, { useState } from "react";
import api from "../api/client";

const EMPTY = { name: "", email: "", subject: "", message: "" };

export default function ContactPage() {
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [banner, setBanner] = useState(null);

  const update = (field) => (e) => {
    setValues((v) => ({ ...v, [field]: e.target.value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  function validate() {
    const next = {};
    if (!values.name.trim()) next.name = "Please tell us your name.";
    if (!values.email.trim()) next.email = "We need an email address to reply to.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(values.email))
      next.email = "That doesn't look like a valid email address.";
    if (!values.message.trim()) next.message = "Please include a message.";
    else if (values.message.trim().length < 10)
      next.message = "Please add a little more detail.";
    return next;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setBanner(null);

    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setSending(true);
    try {
      const res = await api.post("/contact", {
        name: values.name.trim(),
        email: values.email.trim(),
        subject: values.subject.trim(),
        message: values.message.trim(),
      });

      // Success is reported only once the backend confirms the transport
      // accepted the message. This page used to set it locally, so it always
      // claimed success even though nothing was ever sent.
      setBanner({ kind: "success", text: res.data?.message || "Your message has been sent." });
      setValues(EMPTY);
    } catch (err) {
      // Field-level errors from the server take precedence over a banner.
      const fieldErrors = err.response?.data?.errors;
      if (fieldErrors) setErrors(fieldErrors);

      setBanner({
        kind: "error",
        text:
          err.response?.data?.message ||
          "We couldn't send your message. Please check your connection and try again.",
      });
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="es-page">
      <p className="es-eyebrow">Get in touch</p>
      <h1 className="es-h1">Contact EduSpecial</h1>
      <p className="es-lede">
        Questions about a programme, an assessment, or joining a waiting list?
        Send us a message and a member of the team will reply within two working
        days.
      </p>

      <div
        style={{
          display: "grid",
          gap: "2.5rem",
          gridTemplateColumns: "repeat(auto-fit, minmax(18rem, 1fr))",
          alignItems: "start",
        }}
      >
        <section aria-labelledby="form-heading">
          <h2 className="es-h2" id="form-heading">
            Send a message
          </h2>

          {banner && (
            <div
              className={`es-alert es-alert--${banner.kind === "success" ? "success" : "error"}`}
              role={banner.kind === "success" ? "status" : "alert"}
            >
              {banner.text}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="es-field">
              <label className="es-label" htmlFor="contact-name">
                Your name
              </label>
              <input
                id="contact-name"
                className="es-input"
                value={values.name}
                onChange={update("name")}
                aria-invalid={errors.name ? "true" : undefined}
                aria-describedby={errors.name ? "contact-name-error" : undefined}
              />
              {errors.name && (
                <p className="es-error" id="contact-name-error">
                  {errors.name}
                </p>
              )}
            </div>

            <div className="es-field">
              <label className="es-label" htmlFor="contact-email">
                Email address
              </label>
              <input
                id="contact-email"
                type="email"
                className="es-input"
                value={values.email}
                onChange={update("email")}
                aria-invalid={errors.email ? "true" : undefined}
                aria-describedby={errors.email ? "contact-email-error" : undefined}
              />
              {errors.email && (
                <p className="es-error" id="contact-email-error">
                  {errors.email}
                </p>
              )}
            </div>

            <div className="es-field">
              <label className="es-label" htmlFor="contact-subject">
                Subject <span className="es-muted">(optional)</span>
              </label>
              <input
                id="contact-subject"
                className="es-input"
                value={values.subject}
                onChange={update("subject")}
              />
            </div>

            <div className="es-field">
              <label className="es-label" htmlFor="contact-message">
                Message
              </label>
              <textarea
                id="contact-message"
                className="es-textarea"
                rows={5}
                value={values.message}
                onChange={update("message")}
                aria-invalid={errors.message ? "true" : undefined}
                aria-describedby={errors.message ? "contact-message-error" : undefined}
              />
              {errors.message && (
                <p className="es-error" id="contact-message-error">
                  {errors.message}
                </p>
              )}
            </div>

            <button type="submit" className="es-btn es-btn--primary" disabled={sending}>
              {sending ? "Sending…" : "Send message"}
            </button>
          </form>
        </section>

        <section aria-labelledby="details-heading">
          <h2 className="es-h2" id="details-heading">
            Visit or call
          </h2>
          <div className="es-card">
            <div className="es-card__body">
              <h3 className="es-h3">Centre address</h3>
              <p className="es-body" style={{ fontSize: "0.95rem" }}>
                EduSpecial Centre
                <br />
                Hamra Street
                <br />
                Beirut, Lebanon
              </p>

              <h3 className="es-h3" style={{ marginTop: "1.5rem" }}>
                Opening hours
              </h3>
              <table className="es-table" style={{ fontSize: "0.9rem" }}>
                <tbody>
                  <tr>
                    <td>Monday – Thursday</td>
                    <td>08:00 – 17:00</td>
                  </tr>
                  <tr>
                    <td>Friday</td>
                    <td>08:00 – 14:00</td>
                  </tr>
                  <tr>
                    <td>Weekend</td>
                    <td>Closed</td>
                  </tr>
                </tbody>
              </table>

              <h3 className="es-h3" style={{ marginTop: "1.5rem" }}>
                Accessibility
              </h3>
              <p className="es-body" style={{ fontSize: "0.92rem" }}>
                Step-free access throughout, an accessible WC on the ground
                floor, and a low-stimulation waiting room available on request.
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
